// *********************************
// CoolGames Builder JavaScript file
import { Box2D } from "../engine/physics/tbPhysics2";
import { tbVector } from "../engine/tbVector";
import { PhysicsObjectMaterial, PhysicsSettings, PhysicsObjectType } from "../settings/PhysicsSettings";
import { ScoreSettings } from "../settings/Settings";
import { SoundManager } from "../settings/SoundManager";
import { BlockMaterialMap, PhysicsObjectDefinition } from "../settings/blocks";
import { ParticleEffect } from "./ParticleEffect";
import { getContainerForLayer, Layer, globalAddExplosion, physics } from "./SceneController";
import { SpineEffect } from "./SpineEffect";
import { playController } from "./gameController";
// *********************************
export var PhysicsObject = function (def, uid, x, y, angle, container) {
    "use strict";
    this.definitionName = def;
    this.definition = PhysicsObjectDefinition[def];
    this.id = +uid;
    this.material = PhysicsObjectMaterial[this.definition.materialName];
    this.container = container;
    this.width = this.definition.width * PhysicsSettings.physicsObjectScaleFactor;
    this.height = this.definition.height * PhysicsSettings.physicsObjectScaleFactor;
    this.onFirstContact = null;
    this.firstContactFrame = false;
    this.isPig = false;
    this.featherFall = false;
    this.invincible = false;
    this.animationAngle = 0;
    this.settleTimer = 0;
    this.accumulatedScore = 0;
    //body is not null if we change the material.
    //don't recreate the body so the joints don't break
    if (!this.body) {
        var angleRad = (angle / 180 * Math.PI);
        var bodyType = this.material.static ? Box2D.b2_kinematicBody : Box2D.b2_dynamicBody;
        if (this.definition.type == PhysicsObjectType.circle) {
            this.body = physics.createCircle(x, y, this.width / 2, this.material.density, this.material.restitution, this.material.friction, bodyType, angleRad);
            this.body.SetAngularDamping(PhysicsSettings.angularDampeningForCircles);
        }
        else {
            if (this.definition.type == PhysicsObjectType.box) {
                this.body = physics.createBox(x, y, this.width, this.height, this.material.density, this.material.restitution, this.material.friction, bodyType, angleRad);
            }
            else {
                var vertices = [];
                if (this.definition.type == PhysicsObjectType.rightTriangle) {
                    vertices = [new tbVector(this.width / 2, this.height / 2),
                        new tbVector(-this.width / 2, this.height / 2),
                        new tbVector(-this.width / 2, -this.height / 2)];
                }
                else if (this.definition.type == PhysicsObjectType.isoscelesTriangle) {
                    vertices = [new tbVector(this.width / 2, this.height / 2),
                        new tbVector(-this.width / 2, this.height / 2),
                        new tbVector(0, -this.height / 2)];
                }
                var shapeTriangle = physics.createPolygonShape(vertices);
                this.body = physics.createBody(x, y, angleRad, shapeTriangle, bodyType, this.material.density, this.material.restitution, this.material.friction);
                Box2D.destroy(shapeTriangle);
            }
            this.body.SetAngularDamping(PhysicsSettings.angularDampeningForBoxes);
        }
    }
    else {
        var fixture = this.body.GetFixtureList();
        fixture.SetDensity(this.material.density);
        fixture.SetRestitution(this.material.restitution);
        fixture.SetFriction(this.material.friction);
        this.body.ResetMassData();
    }
    this.body.physicsObj = this;
    this.maxHealth = this.computeMaxHealth();
    this.health = this.maxHealth;
    this.firstContact = false;
    //cached physics properties
    this.x = 0;
    this.y = 0;
    this.angle = 0;
    this.radius = 0;
    this.velocity = new tbVector(0, 0);
    this.angular_velocity = 0;
    physics.getBodyData(this.body, this);
    this.scale = this.definition.scale || 1;
    if (this.definition.image) {
        this.image = this.definition.image.clone();
    }
    else {
        this.image = this.definition.spine.clone();
        this.scale = this.definition.animation.scale;
        this.image.setAnimation(this.definition.animation.animation, this.definition.animation.loop);
        this.currentAnimation = this.definition.animation.animation;
    }
    var scaleX = (1 / PhysicsSettings.textureResolution) * this.scale;
    var scaleY = (1 / PhysicsSettings.textureResolution) * this.scale;
    if (this.definition.stretchImage) {
        var scaleX = this.width / this.image.width;
        var scaleY = this.height / this.image.height;
    }
    this.image.setScale(scaleX * PhysicsSettings.extraTextureScale, scaleY * PhysicsSettings.extraTextureScale);
    this.image.hide();
    this.container.addChild(this.image);
};
PhysicsObject.prototype.changeMaterial = function (newMaterial) {
    var newDefinitionIndex = BlockMaterialMap[this.definition.materialName].indexOf(this.definitionName);
    //cleanup
    if (this.image != null) {
        this.image.remove();
        this.image.free();
        this.constructor.call(this, BlockMaterialMap[newMaterial][newDefinitionIndex], this.id, this.x, this.y, this.angle * (180 / Math.PI), this.container);
    }
};
PhysicsObject.prototype.createParticleEffect = function (playSound) {
    "use strict";
    if (this.material.particle) {
        if (playSound) {
            this.playSound(SoundManager.MATERIAL_STATE.DESTROY, this.material.sounds);
        }
        new ParticleEffect(this.x, this.y, this.material.particle);
        new SpineEffect(this.x, this.y, this.material.smokePuff.spine, this.material.smokePuff.name, getContainerForLayer(Layer.smokePuff), (this.definition.width * this.definition.height) / 20);
    }
};
PhysicsObject.prototype.calcDamageThreshold = function () {
    return Math.min(Math.floor(this.definition.damageStates * (1 - (this.health / this.maxHealth))), this.definition.damageStates - 1);
};
PhysicsObject.prototype.draw = function () {
    "use strict";
    if (!this.body || (!PhysicsSettings.shouldDrawGroundBlocks && this.definition.materialName == "Static"))
        return;
    var totalAngle = this.animationAngle + (playController.isMirrored ? 2 * Math.PI : 0) + this.angle;
    if (this.image) {
        this.image.show();
        if (this.definition.damageStates) {
            this.image.gotoAndStop(this.calcDamageThreshold());
            if (this.calcDamageThreshold() === 1 && !this.hasDamage) {
                this.playSound(SoundManager.MATERIAL_STATE.DAMAGE, this.material.sounds);
                this.hasDamage = true;
            }
        }
        this.image.setPosition(this.x, this.y);
        this.image.setRotation(totalAngle);
    }
};
PhysicsObject.prototype.applyDamage = function (damage) {
    if (!this.firstContact && this.onFirstContact) {
        this.onFirstContact();
    }
    if (!this.firstContact)
        this.firstContactFrame = true;
    this.firstContact = true;
    if (this.invincible)
        return;
    if (this.body && this.material.breakable) {
        damage -= this.material.defence;
        if (damage < 0) {
            return;
        }
        this.health -= damage;
        if (this.health <= 0) {
            if (this.definition.deathExplosion) {
                globalAddExplosion(this.x, this.y, this.definition.deathExplosion);
            }
            this.destroy();
            this.finalEffect();
        }
        this.computeScore(damage);
    }
};
PhysicsObject.prototype.finalEffect = function () {
    "use strict";
    this.createParticleEffect(true);
};
PhysicsObject.prototype.computeScore = function (damage) {
    "use strict";
    if (this.health <= 0) {
        this.accumulatedScore = this.isPig ? ScoreSettings.pig : ScoreSettings.block;
    }
    else if (!this.isPig) {
        this.accumulatedScore += damage * ScoreSettings.damageMultiplier;
    }
    this.accumulatedScore = this.accumulatedScore | 0;
};
export function getDamageMultiplier(collider, target) {
    if (collider && target && collider.definition.damageFactors) {
        var ret = collider.definition.damageFactors.damageMultiplier[target.definition.materialName];
        if (ret) {
            return ret;
        }
    }
    return 1;
}
export function getVelocityMultiplier(collider, target) {
    if (collider && target && collider.definition.damageFactors) {
        var ret = collider.definition.damageFactors.velocityMultiplier[target.definition.materialName];
        if (ret) {
            return ret;
        }
    }
    return 1;
}
PhysicsObject.prototype.step = function (timePassed) {
    "use strict";
};
PhysicsObject.prototype.physicsStep = function () {
    "use strict";
    if (this.body) {
        physics.getBodyData(this.body, this);
        if (this.featherFall) {
            var speed = Math.sqrt(this.velocity.x * this.velocity.x + this.velocity.y * this.velocity.y);
            var maxSpeed = 5;
            if (speed > maxSpeed)
                physics.setVelocity(this.body, this.velocity.x * (maxSpeed / speed), this.velocity.y * (maxSpeed / speed));
            physics.setAngularVelocity(this.body, this.angular_velocity / 1.05);
        }
    }
};
PhysicsObject.prototype.setVelocity = function (velocityX, velocityY, angularVelocity) {
    physics.setVelocity(this.body, velocityX, velocityY);
    physics.setAngularVelocity(this.body, angularVelocity);
};
PhysicsObject.prototype.setToIdle = function () {
    "use strict";
    this.setActive(false);
};
PhysicsObject.prototype.setActive = function (bool) {
    "use strict";
    if (this.body) {
        physics.setAwake(this.body, bool);
    }
};
PhysicsObject.prototype.isActive = function (settleThresholdCheck) {
    "use strict";
    if (settleThresholdCheck === undefined) {
        settleThresholdCheck = true;
    }
    if (!this.body) {
        return false;
    }
    var dx = this.velocity.x;
    var dy = this.velocity.y;
    var magnitude = Math.sqrt(dx * dx + dy * dy);
    if (magnitude < 0)
        magnitude *= -1;
    var aV = this.angular_velocity;
    if (aV < 0)
        aV *= -1;
    if (!this.isTNT &&
        settleThresholdCheck === true &&
        !this.isBird &&
        magnitude < PhysicsSettings.physicsSettleThreshold &&
        this.angular_velocity < PhysicsSettings.physicsSettleThreshold_AngularVelocity) {
        this.settleTimer++;
        if (this.settleTimer >= PhysicsSettings.physicsSettleTimer) {
            physics.setAwake(this.body, false);
            this.settleTimer = 0;
            return false;
        }
    }
    else {
        this.settleTimer = 0;
    }
    return (Math.abs(this.angular_velocity) + Math.sqrt(dx * dx + dy * dy) > PhysicsSettings.velocityTolerance);
};
PhysicsObject.prototype.isDead = function () {
    "use strict";
    return ((this.material.breakable && (this.health <= 0)) || (!this.body));
};
PhysicsObject.prototype.removeJoints = function () {
    "use strict";
    if (!playController || !playController.sceneController)
        return;
    var joints = playController.sceneController.jointList;
    for (var i = 0; i < joints.length; i++) {
        if (joints[i].one === this.id || joints[i].two === this.id) {
            joints.splice(i, 1);
            i--;
        }
    }
};
PhysicsObject.prototype.getJointedObjectIDS = function () {
    "use strict";
    if (!playController || !playController.sceneController)
        return false;
    var myJointList = [];
    var joints = playController.sceneController.jointList;
    for (var i = 0; i < joints.length; i++) {
        if (joints[i].one === this.id) {
            myJointList.push(joints[i].two);
        }
        else if (joints[i].two === this.id) {
            myJointList.push(joints[i].one);
        }
    }
    return myJointList;
};
PhysicsObject.prototype.handleSpecialDestroyEvents = function () {
    "use strict";
    if (this.definitionName == "NEW17_NHL_2015_METAL_GRID_A") { // CAGE DESTROYING BALLOONS INSIDE
        var myJointedIDS = this.getJointedObjectIDS();
        if (myJointedIDS) {
            if (myJointedIDS.length > 0) {
                for (var i = 0; i < myJointedIDS.length; i++) {
                    var otherObject = playController.sceneController.findInSceneGraphByID(myJointedIDS[i]);
                    otherObject[0].body.physicsObj.destroy();
                }
            }
        }
    }
};
PhysicsObject.prototype.destroy = function () {
    "use strict";
    if (!this.body) {
        return;
    }
    this.handleSpecialDestroyEvents();
    this.removeJoints();
    this.health = -1;
    physics.destroyBody(this.body);
    this.body = null;
    this.image.remove();
    this.image.free();
    this.image = null;
};
PhysicsObject.prototype.computeMaxHealth = function () {
    "use strict";
    var ret = 0;
    var h = this.definition.height;
    var w = this.definition.width;
    var strength = this.material.strength;
    if (this.definition.type == PhysicsObjectType.circle) {
        ret = Math.PI * ((h / 2) * (h / 2)) * strength;
    }
    else if (this.definition.type == PhysicsObjectType.box) {
        ret = (w * h) * ((h / w) * 0.4 + 0.6) * strength;
    }
    else if (this.definition.type == PhysicsObjectType.rightTriangle ||
        this.definition.type == PhysicsObjectType.isoscelesTriangle) {
        ret = ((w * h) / 3) * strength;
    }
    return ret;
};
PhysicsObject.prototype.snapAndGetSaveData = function () {
    "use strict";
    var x = this.x;
    var y = this.y;
    var a = this.angle;
    var h = this.health / this.maxHealth;
    var m = PhysicsObjectDefinition[this.definitionName].generatedUID;
    var vx = this.velocity.x;
    var vy = this.velocity.y;
    var va = this.angular_velocity;
    //this.loadData(x, y, a, h, this.definitionName);
    return [this.id, x, y, a, h, m, vx, vy, va];
};
PhysicsObject.prototype.loadData = function (x, y, a, h, m) {
    "use strict";
    this.destroy();
    this.constructor.call(this, m, this.id, x, y, a * (180 / Math.PI), this.container);
    this.health = this.maxHealth * h;
};
PhysicsObject.prototype.lookAt = function (x, y) {
    this.animationAngle = Math.atan2(y - this.y, x - this.x);
};
PhysicsObject.prototype.playSound = function (state, collection, delay) {
    "use strict";
    SoundManager.playEffect(state, collection, delay);
};
